import {
  ACRESCIMO_TEMPO_PADRAO,
  META_PONTOS_DIA_PADRAO,
  MINUTOS_DIA_PADRAO,
  type ConfigTempoEmbalar,
} from './tempoEmbalar';

export const STORAGE_TEMPO_EMBALAR_LS = 'tempo-embalar-dados-ade';

const DB_NAME = 'ade-tempo-embalar-db';
const DB_VERSION = 1;
const STORE_DADOS = 'dados';
const KEY_DADOS = 'principal';

export interface EntradaVolumePersistida {
  tempoStr: string;
  quantidadeStr: string;
}

export interface DadosPersistidosEmbalar {
  config: ConfigTempoEmbalar;
  volumesPorProduto: Record<string, number>;
  entradas: Record<string, EntradaVolumePersistida>;
  /**
   * Legado: observação única por produto (chave = nome normalizado).
   * Mantido para não perder dados antigos; a fonte atual é observacoesPorVolume.
   */
  observacoesPorProduto: Record<string, string>;
  /**
   * Observação por linha de medição (chave = nomeNorm|volume ou nomeNorm|volume#N).
   * Array legado com várias strings na mesma chave é migrado para várias medições.
   */
  observacoesPorVolume: Record<string, string[]>;
  /**
   * Quantidade de linhas de medição por volume (chave = nomeNorm|volume). Mínimo 1.
   * Linhas extras: nomeNorm|volume#2, #3, … (tempo/qtd/obs próprios para comparar).
   */
  medicoesPorVolume: Record<string, number>;
}

function configPadrao(): ConfigTempoEmbalar {
  return {
    metaPontosDia: META_PONTOS_DIA_PADRAO,
    minutosDia: MINUTOS_DIA_PADRAO,
    percentualAcrescimoTempo: ACRESCIMO_TEMPO_PADRAO,
  };
}

export function dadosEmbalarVazios(): DadosPersistidosEmbalar {
  return {
    config: configPadrao(),
    volumesPorProduto: {},
    entradas: {},
    observacoesPorProduto: {},
    observacoesPorVolume: {},
    medicoesPorVolume: {},
  };
}

/** Chave da 1ª medição = nome|vol; extras = nome|vol#2, #3… */
export function chaveMedicaoEmbalar(nomeNorm: string, volume: number, medicao: number): string {
  const base = `${nomeNorm}|${volume}`;
  return medicao <= 1 ? base : `${base}#${medicao}`;
}

export function chaveBaseVolumeEmbalar(chaveMedicao: string): string {
  const hash = chaveMedicao.indexOf('#');
  return hash >= 0 ? chaveMedicao.slice(0, hash) : chaveMedicao;
}

function normalizarListaObs(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    const lista = raw.map((o) => String(o ?? ''));
    return lista.length > 0 ? lista : [''];
  }
  if (typeof raw === 'string') return [raw];
  return [''];
}

function normalizarMapaObsVolume(
  raw: unknown
): Record<string, string[]> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    out[k] = normalizarListaObs(v);
  }
  return out;
}

function normalizarMedicoesPorVolume(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = Math.round(Number(v));
    if (Number.isFinite(n) && n >= 1) out[k] = Math.min(20, n);
  }
  return out;
}

/** Migra observação legada por produto → Vol. 1, sem apagar o legado. */
function migrarObservacoesPorVolume(
  porProduto: Record<string, string>,
  porVolume: Record<string, string[]>
): Record<string, string[]> {
  const out: Record<string, string[]> = { ...porVolume };
  for (const [nomeNorm, texto] of Object.entries(porProduto)) {
    const t = String(texto || '').trim();
    if (!t) continue;
    const chaveVol1 = `${nomeNorm}|1`;
    const atual = out[chaveVol1];
    const temConteudo =
      Array.isArray(atual) && atual.some((o) => String(o || '').trim().length > 0);
    if (!temConteudo) {
      out[chaveVol1] = [String(texto || '')];
    }
  }
  return out;
}

/**
 * Se ainda houver várias obs na mesma chave (versão anterior),
 * espalha para medições #2, #3… sem perder texto.
 */
function migrarObsMultiParaMedicoes(
  observacoesPorVolume: Record<string, string[]>,
  medicoesPorVolume: Record<string, number>,
  entradas: Record<string, EntradaVolumePersistida>
): {
  observacoesPorVolume: Record<string, string[]>;
  medicoesPorVolume: Record<string, number>;
  entradas: Record<string, EntradaVolumePersistida>;
} {
  const obsOut = { ...observacoesPorVolume };
  const medOut = { ...medicoesPorVolume };
  const entOut = { ...entradas };

  for (const [chave, lista] of Object.entries(observacoesPorVolume)) {
    if (chave.includes('#')) continue;
    const preenchidas = (lista || []).map((o) => String(o ?? '')).filter((o) => o.trim());
    if (preenchidas.length <= 1) {
      if ((lista || []).length > 1) obsOut[chave] = [lista[0] ?? ''];
      continue;
    }
    const jaTemMedicoes = (medOut[chave] ?? 1) > 1;
    const jaTemExtras = Object.keys(entOut).some((k) => k.startsWith(`${chave}#`));
    if (jaTemMedicoes || jaTemExtras) {
      obsOut[chave] = [preenchidas[0]];
      continue;
    }
    medOut[chave] = preenchidas.length;
    obsOut[chave] = [preenchidas[0]];
    for (let i = 1; i < preenchidas.length; i += 1) {
      const ck = `${chave}#${i + 1}`;
      obsOut[ck] = [preenchidas[i]];
      if (!entOut[ck]) entOut[ck] = { tempoStr: '', quantidadeStr: '' };
    }
  }
  return { observacoesPorVolume: obsOut, medicoesPorVolume: medOut, entradas: entOut };
}

/** Espelha a 1ª observação preenchida do Vol. 1 de volta no mapa legado. */
function espelharObsProduto(
  porVolume: Record<string, string[]>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [chave, lista] of Object.entries(porVolume)) {
    if (chave.includes('#')) continue;
    const sep = chave.lastIndexOf('|');
    if (sep <= 0) continue;
    const vol = chave.slice(sep + 1);
    if (vol !== '1') continue;
    const nomeNorm = chave.slice(0, sep);
    const primeira = (lista || []).find((o) => String(o || '').trim()) ?? '';
    if (String(primeira).trim()) out[nomeNorm] = primeira;
  }
  return out;
}

function normalizarDados(raw: Partial<DadosPersistidosEmbalar> | null | undefined): DadosPersistidosEmbalar {
  if (!raw || typeof raw !== 'object') return dadosEmbalarVazios();
  const acrescimo = Number(raw.config?.percentualAcrescimoTempo);
  const minutos = Number(raw.config?.minutosDia);
  const meta = Number(raw.config?.metaPontosDia);
  const observacoesPorProduto =
    raw.observacoesPorProduto && typeof raw.observacoesPorProduto === 'object'
      ? Object.fromEntries(
          Object.entries(raw.observacoesPorProduto).map(([k, v]) => [k, String(v ?? '')])
        )
      : {};
  let observacoesPorVolume = migrarObservacoesPorVolume(
    observacoesPorProduto,
    normalizarMapaObsVolume(raw.observacoesPorVolume)
  );
  let medicoesPorVolume = normalizarMedicoesPorVolume(raw.medicoesPorVolume);
  let entradas = raw.entradas && typeof raw.entradas === 'object' ? { ...raw.entradas } : {};
  const migrado = migrarObsMultiParaMedicoes(observacoesPorVolume, medicoesPorVolume, entradas);
  observacoesPorVolume = migrado.observacoesPorVolume;
  medicoesPorVolume = migrado.medicoesPorVolume;
  entradas = migrado.entradas;

  return {
    config: {
      metaPontosDia: Number.isFinite(meta) && meta > 0 ? meta : META_PONTOS_DIA_PADRAO,
      minutosDia: Number.isFinite(minutos) && minutos > 0 ? minutos : MINUTOS_DIA_PADRAO,
      percentualAcrescimoTempo:
        Number.isFinite(acrescimo) && acrescimo >= 0 ? acrescimo : ACRESCIMO_TEMPO_PADRAO,
    },
    volumesPorProduto:
      raw.volumesPorProduto && typeof raw.volumesPorProduto === 'object'
        ? raw.volumesPorProduto
        : {},
    entradas,
    observacoesPorProduto: {
      ...observacoesPorProduto,
      ...espelharObsProduto(observacoesPorVolume),
    },
    observacoesPorVolume,
    medicoesPorVolume,
  };
}

function mesclarListasObs(a: string[], b: string[]): string[] {
  const limpasA = (a || []).map((o) => String(o ?? '')).filter((o) => o.trim());
  const limpasB = (b || []).map((o) => String(o ?? '')).filter((o) => o.trim());
  if (limpasB.length > limpasA.length) return limpasB.length ? limpasB : [''];
  if (limpasA.length > limpasB.length) return limpasA.length ? limpasA : [''];
  const lenA = limpasA.join('\n').length;
  const lenB = limpasB.join('\n').length;
  const base = lenB > lenA ? limpasB : limpasA;
  return base.length ? base : [''];
}

function mesclarObservacoesVolume(
  a: Record<string, string[]>,
  b: Record<string, string[]>
): Record<string, string[]> {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out: Record<string, string[]> = {};
  for (const k of keys) {
    out[k] = mesclarListasObs(a[k] || [''], b[k] || ['']);
  }
  return out;
}

function mesclarObservacoes(
  a: Record<string, string>,
  b: Record<string, string>
): Record<string, string> {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out: Record<string, string> = {};
  for (const k of keys) {
    const ta = String(a[k] || '').trim();
    const tb = String(b[k] || '').trim();
    if (ta.length >= tb.length) out[k] = a[k] ?? '';
    else out[k] = b[k] ?? '';
  }
  return out;
}

function contarObservacoesPreenchidas(d: DadosPersistidosEmbalar): number {
  let n = 0;
  for (const lista of Object.values(d.observacoesPorVolume || {})) {
    n += (lista || []).filter((o) => String(o || '').trim()).length;
  }
  if (n > 0) return n;
  return Object.values(d.observacoesPorProduto || {}).filter((o) => String(o || '').trim()).length;
}

function contarEntradasPreenchidas(d: DadosPersistidosEmbalar): number {
  return Object.values(d.entradas).filter(
    (e) => String(e?.tempoStr || '').trim() || String(e?.quantidadeStr || '').trim()
  ).length;
}

function mesclarMedicoesPorVolume(
  a: Record<string, number>,
  b: Record<string, number>
): Record<string, number> {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out: Record<string, number> = {};
  for (const k of keys) {
    out[k] = Math.max(a[k] ?? 1, b[k] ?? 1);
  }
  return out;
}

function mesclarCamposObs(
  a: DadosPersistidosEmbalar,
  b: DadosPersistidosEmbalar
): Pick<
  DadosPersistidosEmbalar,
  'observacoesPorProduto' | 'observacoesPorVolume' | 'medicoesPorVolume'
> {
  const observacoesPorVolume = mesclarObservacoesVolume(
    a.observacoesPorVolume || {},
    b.observacoesPorVolume || {}
  );
  return {
    observacoesPorVolume,
    observacoesPorProduto: mesclarObservacoes(
      {
        ...(a.observacoesPorProduto || {}),
        ...espelharObsProduto(a.observacoesPorVolume || {}),
      },
      {
        ...(b.observacoesPorProduto || {}),
        ...espelharObsProduto(b.observacoesPorVolume || {}),
      }
    ),
    medicoesPorVolume: mesclarMedicoesPorVolume(
      a.medicoesPorVolume || {},
      b.medicoesPorVolume || {}
    ),
  };
}

/** Prefere o conjunto com mais dados preenchidos (evita sobrescrever com estado vazio). */
export function escolherMelhorDadosEmbalar(
  a: DadosPersistidosEmbalar,
  b: DadosPersistidosEmbalar
): DadosPersistidosEmbalar {
  const ca = contarEntradasPreenchidas(a);
  const cb = contarEntradasPreenchidas(b);
  if (cb > ca) return { ...b, ...mesclarCamposObs(a, b) };
  if (ca > cb) return { ...a, ...mesclarCamposObs(a, b) };
  const volA = Object.keys(a.volumesPorProduto).length;
  const volB = Object.keys(b.volumesPorProduto).length;
  const obsA = contarObservacoesPreenchidas(a);
  const obsB = contarObservacoesPreenchidas(b);
  const base = volB > volA || obsB > obsA ? b : a;
  return {
    ...base,
    ...mesclarCamposObs(a, b),
  };
}

export function lerDadosTempoEmbalarLocalStorage(): DadosPersistidosEmbalar {
  try {
    const raw = localStorage.getItem(STORAGE_TEMPO_EMBALAR_LS);
    if (!raw) return dadosEmbalarVazios();
    return normalizarDados(JSON.parse(raw) as Partial<DadosPersistidosEmbalar>);
  } catch {
    return dadosEmbalarVazios();
  }
}

function abrirDbTempoEmbalar(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_DADOS)) {
          db.createObjectStore(STORE_DADOS);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function lerDadosTempoEmbalarIndexedDB(): Promise<DadosPersistidosEmbalar | null> {
  const db = await abrirDbTempoEmbalar();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_DADOS, 'readonly');
      const req = tx.objectStore(STORE_DADOS).get(KEY_DADOS);
      req.onsuccess = () => {
        const val = req.result as Partial<DadosPersistidosEmbalar> | undefined;
        resolve(val ? normalizarDados(val) : null);
      };
      req.onerror = () => resolve(null);
      tx.oncomplete = () => db.close();
      tx.onabort = () => db.close();
    } catch {
      db.close();
      resolve(null);
    }
  });
}

async function salvarDadosTempoEmbalarIndexedDB(dados: DadosPersistidosEmbalar): Promise<void> {
  const db = await abrirDbTempoEmbalar();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_DADOS, 'readwrite');
      tx.objectStore(STORE_DADOS).put(dados, KEY_DADOS);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        resolve();
      };
      tx.onabort = () => {
        db.close();
        resolve();
      };
    } catch {
      db.close();
      resolve();
    }
  });
}

/** Grava em localStorage (síncrono) + IndexedDB (NW.js persiste melhor ao fechar o app). */
export function persistirDadosTempoEmbalar(dados: DadosPersistidosEmbalar): void {
  const payload = normalizarDados(dados);
  try {
    localStorage.setItem(STORAGE_TEMPO_EMBALAR_LS, JSON.stringify(payload));
  } catch {
    /* quota */
  }
  void salvarDadosTempoEmbalarIndexedDB(payload);
}

export async function carregarDadosTempoEmbalarCompleto(): Promise<DadosPersistidosEmbalar> {
  const ls = lerDadosTempoEmbalarLocalStorage();
  const idb = await lerDadosTempoEmbalarIndexedDB();
  if (!idb) return ls;
  return escolherMelhorDadosEmbalar(ls, idb);
}
